import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Image, Trophy, MessageSquare, Mail, TrendingUp, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

const quickLinks = [
  { path: "/admin/gallery", icon: <Image size={20} />, label: "Manage Gallery", color: "#6366f1", bg: "#eef2ff" },
  { path: "/admin/toppers", icon: <Trophy size={20} />, label: "Manage Toppers", color: "#f59e0b", bg: "#fffbeb" },
  { path: "/admin/testimonials", icon: <MessageSquare size={20} />, label: "Manage Testimonials", color: "#10b981", bg: "#f0fdf4" },
  { path: "/admin/enquiries", icon: <Mail size={20} />, label: "View Enquiries", color: "#ef4444", bg: "#fef2f2" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState({ enquiries: 0, gallery: 0, toppers: 0, testimonials: 0 });
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, e] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/stats`, { headers: getHeaders() }),
          axios.get(`${BACKEND_URL}/api/admin/enquiries`, { headers: getHeaders() }),
        ]);
        setStats(s.data);
        setEnquiries(e.data.slice(0, 5));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, []);

  const statCards = [
    { label: "Total Enquiries", value: stats.enquiries, icon: <Mail size={22} />, color: "#ef4444", bg: "#fef2f2" },
    { label: "Gallery Images", value: stats.gallery, icon: <Image size={22} />, color: "#6366f1", bg: "#eef2ff" },
    { label: "Toppers", value: stats.toppers, icon: <Trophy size={22} />, color: "#f59e0b", bg: "#fffbeb" },
    { label: "Testimonials", value: stats.testimonials, icon: <MessageSquare size={22} />, color: "#10b981", bg: "#f0fdf4" },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-gray-400">Loading...</div></div>;

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <div>
        <h2 className="text-2xl font-extrabold mb-1" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>
          Welcome back, Admin!
        </h2>
        <p className="text-sm text-gray-500">Here's what's happening with Trident Academy's website.</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm" data-testid={`stat-card-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: s.bg, color: s.color }}>
                {s.icon}
              </div>
              <TrendingUp size={14} className="text-green-400" />
            </div>
            <div className="text-3xl font-extrabold" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>{s.value}</div>
            <div className="text-xs text-gray-400 mt-1 font-semibold">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <h3 className="font-bold text-base mb-4" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {quickLinks.map((q, i) => (
            <Link
              key={i}
              to={q.path}
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-100 hover:shadow-md transition-all text-center"
              style={{ backgroundColor: q.bg }}
            >
              <div style={{ color: q.color }}>{q.icon}</div>
              <span className="text-xs font-semibold" style={{ color: q.color }}>{q.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Enquiries */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-base" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>Recent Enquiries</h3>
          <Link to="/admin/enquiries" className="text-xs font-bold flex items-center gap-1" style={{ color: "#0A2540" }}>
            View All <ExternalLink size={12} />
          </Link>
        </div>
        {enquiries.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No enquiries yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Name</th>
                  <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Course</th>
                  <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Phone</th>
                  <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Date</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map((e, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2.5 px-3 font-semibold" style={{ color: "#0A2540" }}>{e.full_name}</td>
                    <td className="py-2.5 px-3 text-gray-500">{e.course}</td>
                    <td className="py-2.5 px-3 text-gray-500">{e.phone}</td>
                    <td className="py-2.5 px-3 text-gray-400 text-xs">{new Date(e.created_at).toLocaleDateString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
